/**
 * The seam between the application shell and whatever identity provider the
 * environment is configured with.
 *
 * Issue #18 replaces the adapter implementation; nothing above this interface
 * changes when it does. The browser only ever holds an access token — ADR 0001
 * forbids issuing storage credentials to it.
 */

export interface AuthIdentity {
  /** Stable opaque identifier for the authenticated principal. */
  subject: string;
  displayName: string;
  email?: string;
}

export interface AuthAdapter {
  /** Name used in diagnostics and on the fail-closed screens. */
  readonly kind: "oidc" | "dev-stub";

  /**
   * Whether signing out can actually end the session.
   *
   * False for the development stub, which has no session to end: the boot
   * sequence starts a new one immediately, so a Sign out control there would be
   * a control that does nothing. Callers must disable it with a reason rather
   * than render it inert.
   */
  readonly supportsSignOut: boolean;

  /**
   * Whether a deliberate account switch is possible. False for the
   * development stub, whose identity is fixed; callers disable the control
   * with a reason, exactly as for sign-out.
   */
  readonly supportsAccountSwitch: boolean;

  /**
   * Completes any pending redirect, restores an existing session, and resolves
   * once the adapter can answer `getIdentity()`. It must not throw for the
   * ordinary "nobody is signed in yet" case — that is reported by
   * `getIdentity()` returning null.
   */
  initialize(): Promise<void>;

  /** Current access token, or null when unauthenticated. */
  getAccessToken(): Promise<string | null>;

  getIdentity(): AuthIdentity | null;

  /** Begins interactive sign-in. May navigate away from the document. */
  signIn(): Promise<void>;

  /**
   * Begins an interactive sign-in that forces the provider's account picker,
   * so the user can deliberately continue as a different account. Without the
   * forced picker, an existing provider SSO session signs the current account
   * straight back in and the control does nothing. May navigate away.
   */
  switchAccount(): Promise<void>;

  /**
   * Clears this tab's locally cached session WITHOUT touching the provider.
   *
   * For the tab that did not initiate sign-out: when another tab announces a
   * sign-out, this tab's own cache still holds tokens, and leaving them in
   * place would let a reload boot straight back into an authenticated UI.
   */
  clearLocalSession(): Promise<void>;

  /**
   * Ends the session and owns where the user lands afterwards — it may navigate
   * away from the document.
   *
   * Callers must not navigate themselves. An OIDC sign-out has to reach the
   * provider's end-session endpoint, and a caller-initiated `location.assign`
   * races that redirect and cancels it, leaving the provider session alive.
   */
  signOut(): Promise<void>;
}

export class AuthInitializationError extends Error {
  readonly details: string[];

  constructor(message: string, details: string[] = []) {
    super(message);
    this.name = "AuthInitializationError";
    this.details = details;
  }
}
